import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import type { ArticleSection } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoobuy.baby';

function SectionRenderer({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 pt-6 pb-2">
          {section.text}
        </h2>
      );

    case 'paragraph':
      return (
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          {section.text}
        </p>
      );

    case 'highlight-box':
      return (
        <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl p-5 md:p-6 my-6">
          <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">
            Destaques:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {section.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <i className="ri-check-line text-accent-600 text-sm flex-shrink-0"></i>
                <span className="text-sm text-foreground-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case 'step-list':
      return (
        <div className="my-6">
          <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">
            Passo a passo:
          </h3>
          <div className="space-y-4">
            {section.items?.map((s, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-primary-600">{s.step}</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900">{s.title}</h4>
                  <p className="text-sm text-foreground-600">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'faq-list':
      return (
        <div className="space-y-4 my-6">
          {section.items?.map((faq, i) => (
            <div key={i} className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/50">
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                {faq.q}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      );

    case 'category-links':
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 my-6">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-up-line text-xs text-accent-500"></i>
              {cat.namePt}
            </a>
          ))}
        </div>
      );

    case 'cta-buttons':
      return null;

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <title>Artigo não encontrado | HoobuySpreadsheet</title>
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Artigo não encontrado</h1>
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao Início
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const hasFaq = article.sections.some((s) => s.type === 'faq-list');
  const articleUrl = SITE_URL + '/artigo/' + article.slug;

  return (
    <div className="min-h-screen bg-background-50">
      <title>{article.title} | HoobuySpreadsheet</title>
      <Navbar />

      <main>
        <article>
          <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
                <a
                  href="/"
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  className="hover:text-primary-500 transition-colors cursor-pointer"
                >
                  Início
                </a>
                <span className="mx-2">/</span>
                <a
                  href="/guia-de-compras"
                  onClick={(e) => { e.preventDefault(); navigate('/guia-de-compras'); }}
                  className="hover:text-primary-500 transition-colors cursor-pointer"
                >
                  Guia de Compras
                </a>
                <span className="mx-2">/</span>
                <span className="text-foreground-400">Artigo</span>
              </nav>

              <span className="inline-block px-4 py-1.5 text-xs font-semibold bg-accent-100 text-accent-700 rounded-full mb-4">
                Guia de Compras
              </span>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed mb-6">
                {article.summary}
              </p>

              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  Atualizado: Junho 2026
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  Leitura: {Math.max(3, Math.floor(article.sections.filter(s => s.type === 'paragraph').length * 1.2))} min
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  Por Equipe HoobuySpreadsheet
                </span>
              </div>
            </div>
          </section>

          <div className="relative w-full h-56 md:h-80 overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/40 to-transparent"></div>
          </div>

          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="space-y-5 text-sm md:text-base">
                {article.sections.map((section, idx) => (
                  <SectionRenderer key={idx} section={section} />
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/50 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  hoobuy sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href="/"
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold bg-foreground-900 text-white rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar ao Início
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.image,
            author: { '@type': 'Organization', name: 'HoobuySpreadsheet' },
            publisher: { '@type': 'Organization', name: 'HoobuySpreadsheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg' } },
            datePublished: '2026-06-20',
            dateModified: '2026-06-25',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': articleUrl,
            },
            ...(hasFaq ? {
              mainEntity: article.sections
                .filter(s => s.type === 'faq-list')
                .flatMap(s => (s.items || []).map(faq => ({
                  '@type': 'Question' as const,
                  name: faq.q,
                  acceptedAnswer: {
                    '@type': 'Answer' as const,
                    text: faq.a,
                  },
                }))),
            } : {}),
          }),
        }}
      />
    </div>
  );
}